import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import SearchIcon from "@material-ui/icons/Search";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ColorizeIcon from "@material-ui/icons/Colorize";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ChatbotModal from "../../components/ChatbotModal";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import ColorPicker, {
  isValidHexColor,
  normalizeHexColor,
  saveCustomColor,
} from "../../components/ColorPicker";

const SOURCE_STAGE_ID = "source-chats";
const SOURCE_TICKETS_PAGE_SIZE = 20;
const TICKET_DRAG_TYPE = "application/x-funnel-ticket";
const STAGE_DRAG_TYPE = "application/x-funnel-stage";
const NO_STAGE_SEQUENCE = "__no_stage_sequence__";
const ALL_FUNNEL_USERS = "__all_funnel_users__";
const NO_FUNNEL_SELECTION = "__no_funnel_selection__";
const DEFAULT_STAGE_COLOR = "#E8F3FF";
const STAGE_COLORS = [
  { name: "Azul cielo", value: "#E8F3FF", accent: "#2576D2" },
  { name: "Menta", value: "#E7F8EF", accent: "#0F9F6E" },
  { name: "Lavanda", value: "#F0ECFF", accent: "#7C3AED" },
  { name: "Durazno", value: "#FFF0E6", accent: "#F97316" },
  { name: "Rosa suave", value: "#FFEAF1", accent: "#E11D62" },
  { name: "Amarillo crema", value: "#FFF7D6", accent: "#D99A00" },
  { name: "Verde agua", value: "#E4F7F5", accent: "#0891B2" },
  { name: "Lila", value: "#F7E9FF", accent: "#A21CAF" },
  { name: "Coral suave", value: "#FFE7E1", accent: "#F04F3E" },
  { name: "Gris azulado", value: "#EEF3F7", accent: "#4B6B88" },
];

const getNextStageColor = stages => {
  const usedColors = new Set((stages || []).map(stage => stage.color));
  return (
    STAGE_COLORS.find(color => !usedColors.has(color.value)) ||
    STAGE_COLORS[(stages || []).length % STAGE_COLORS.length]
  ).value;
};

const normalizeStage = stage => ({
  id: stage.id,
  name: stage.name || "Etapa",
  color: stage.color || DEFAULT_STAGE_COLOR,
  position: Number(stage.position || 0),
  onEnterSequenceId: stage.onEnterSequenceId || "",
  onLeaveSequenceId: stage.onLeaveSequenceId || "",
});

const normalizeFunnels = funnels => {
  if (!Array.isArray(funnels)) return [];

  return funnels.map(funnel => ({
    id: funnel.id,
    name: funnel.name || "Embudo",
    allowedUserIds: Array.isArray(funnel.allowedUserIds)
      ? funnel.allowedUserIds.map(Number).filter(Boolean)
      : [],
    stages: Array.isArray(funnel.stages) && funnel.stages.length
      ? funnel.stages.map(normalizeStage)
      : [],
    ticketStages: (funnel.tickets || []).reduce((acc, item) => {
      acc[String(item.ticketId)] = item.stageId;
      return acc;
    }, {}),
    ticketPositions: (funnel.tickets || []).reduce((acc, item) => {
      acc[String(item.ticketId)] = Number(item.position || 0);
      return acc;
    }, {}),
  }));
};

const getTicketCard = ticket => ({
  id: String(ticket.id),
  contact: ticket.contact?.name || "Contacto",
  number: ticket.contact?.number || "",
  profilePicUrl: ticket.contact?.profilePicUrl || "",
  agent: ticket.user?.name || "Sin agente",
  agentProfilePicUrl: ticket.user?.profilePicUrl || "",
  queue: ticket.queue?.name || "Sin área",
  queueColor: ticket.queue?.color || "#7C7C7C",
  connection: ticket.connection?.name || ticket.connection?.number || "",
  connectionNumber: ticket.connection?.number || "",
  connectionId: ticket.connection?.id || ticket.connectionId || "",
  queueId: ticket.queue?.id || ticket.queueId || "",
  agentId: ticket.user?.id || ticket.userId || "",
  labels: ticket.labels || [],
});

const getReadableTextColor = color => {
  const hex = (color || "#ffffff").replace("#", "");
  const normalized =
    hex.length === 3
      ? hex.split("").map(char => `${char}${char}`).join("")
      : hex.padEnd(6, "f").slice(0, 6);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance > 150 ? "#243040" : "#ffffff";
};

const hexToRgb = color => {
  const normalized = String(color || DEFAULT_STAGE_COLOR).replace("#", "");
  const hex =
    normalized.length === 3
      ? normalized.split("").map(char => `${char}${char}`).join("")
      : normalized.padEnd(6, "f").slice(0, 6);

  return {
    red: parseInt(hex.slice(0, 2), 16),
    green: parseInt(hex.slice(2, 4), 16),
    blue: parseInt(hex.slice(4, 6), 16),
  };
};

const rgbToHex = ({ red, green, blue }) =>
  `#${[red, green, blue]
    .map(value => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")}`;

const mixColor = (color, target, amount) => {
  const sourceRgb = hexToRgb(color);
  const targetRgb = hexToRgb(target);

  return rgbToHex({
    red: sourceRgb.red + (targetRgb.red - sourceRgb.red) * amount,
    green: sourceRgb.green + (targetRgb.green - sourceRgb.green) * amount,
    blue: sourceRgb.blue + (targetRgb.blue - sourceRgb.blue) * amount,
  });
};

const getStageColors = color => {
  const stageColor = color || DEFAULT_STAGE_COLOR;
  const preset = STAGE_COLORS.find(item => item.value.toLowerCase() === String(stageColor).toLowerCase());
  const accent = preset?.accent || stageColor;
  const base = mixColor(accent, "#ffffff", 0.88);
  const header = mixColor(accent, "#ffffff", 0.82);
  const border = mixColor(accent, "#ffffff", 0.32);
  const chipBackground = mixColor(accent, "#ffffff", 0.72);
  const mutedIcon = mixColor(accent, "#2f3a45", 0.34);

  return {
    base,
    header,
    accent,
    border,
    chipBackground,
    mutedIcon,
    chipText: getReadableTextColor(chipBackground),
  };
};

const useStyles = makeStyles(theme => ({
  boardPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(2),
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
  },
  headerActions: {
    flex: "1 1 auto",
    marginLeft: 0,
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
    },
  },
  inlineForm: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: "0 1 auto",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      width: "100%",
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
  selector: {
    flex: "0 1 220px",
    minWidth: 180,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: 0,
    },
  },
  funnelSelectorGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flex: "0 1 auto",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  funnelActionButton: {
    flex: "0 0 auto",
  },
  sheetConfigFields: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 140px",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sheetConfigStatus: {
    marginTop: theme.spacing(1),
    minHeight: 20,
  },
  addInput: {
    width: 180,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  filterControl: {
    flex: "0 1 180px",
    minWidth: 160,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: 0,
    },
  },
  board: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "minmax(280px, 320px)",
    gap: theme.spacing(2),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  stage: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: 520,
    maxHeight: "calc(100vh - 180px)",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },
  stageColorBar: {
    height: 5,
    flex: "0 0 auto",
  },
  sourceStage: {
    borderColor: "rgba(37, 118, 210, 0.35)",
  },
  stageDraggingOver: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  stageColumnDraggingOver: {
    boxShadow: `0 8px 18px rgba(0, 0, 0, 0.14)`,
    "&:before": {
      content: '""',
      position: "absolute",
      top: 8,
      bottom: 8,
      width: 6,
      borderRadius: 999,
      backgroundColor: theme.palette.primary.main,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      zIndex: 3,
    },
  },
  stageColumnDropBefore: {
    "&:before": {
      left: 6,
    },
  },
  stageColumnDropAfter: {
    "&:before": {
      right: 6,
    },
  },
  ticketDropBefore: {
    boxShadow: `0 -3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`,
  },
  ticketDropAfter: {
    boxShadow: `0 3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`,
  },
  stageHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  stageCount: {
    fontWeight: 700,
    border: "1px solid rgba(0, 0, 0, 0.08)",
  },
  sourceHeader: {
    display: "block",
  },
  stageTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  stageTitle: {
    flex: 1,
    fontWeight: 700,
  },
  stageDragButton: {
    cursor: "grab",
    color: theme.palette.text.secondary,
    padding: 4,
    "&:active": {
      cursor: "grabbing",
    },
  },
  searchField: {
    width: "100%",
  },
  stageBody: {
    flex: 1,
    padding: theme.spacing(1.25),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  ticketCard: {
    position: "relative",
    marginBottom: theme.spacing(1),
    overflow: "hidden",
    borderRadius: 6,
    border: "1px solid rgba(0, 0, 0, 0.10)",
    cursor: "grab",
    backgroundColor: theme.palette.background.paper,
    transition: "box-shadow 120ms ease, transform 120ms ease",
    "&:active": {
      cursor: "grabbing",
    },
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.10)",
      transform: "translateY(-1px)",
    },
  },
  ticketListItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(4),
    alignItems: "center",
  },
  ticketQueueColor: {
    width: 6,
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  ticketTopLine: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
  },
  labelDots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    marginLeft: "auto",
    flex: "none",
    maxWidth: 82,
  },
  labelDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  ticketNumber: {
    display: "block",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  ticketMetaTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    width: "100%",
    padding: theme.spacing(0.75, 1, 1, 5.75),
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
    backgroundColor: theme.palette.background.default,
  },
  dragIcon: {
    color: theme.palette.text.secondary,
    flex: "none",
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    maxWidth: 132,
    minHeight: 24,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tagText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  agentTag: {
    color: "#ffffff",
    backgroundColor: "#455A64",
  },
  connectionTag: {
    color: "#ffffff",
    backgroundColor: "#148040",
  },
  tagAvatar: {
    width: 18,
    height: 18,
    fontSize: 11,
  },
  openChatButton: {
    position: "absolute",
    top: 26,
    right: 4,
    zIndex: 1,
  },
  emptyStage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 120,
    color: theme.palette.text.secondary,
    border: "1px dashed rgba(0, 0, 0, 0.18)",
    borderRadius: 8,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  modalControl: {
    marginBottom: theme.spacing(2),
  },
  sequenceActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
  },
  colorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  colorOption: {
    height: 34,
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.12)",
    },
  },
  colorOptionSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const Funnels = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const canManageFunnels = ["admin", "superadmin", "owner"].includes(
    String(user?.profile || "").toLowerCase()
  );
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const [funnels, setFunnels] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketsCount, setTicketsCount] = useState(0);
  const [sequences, setSequences] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [stageName, setStageName] = useState("");
  const [funnelName, setFunnelName] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [draggingStageId, setDraggingStageId] = useState("");
  const [draggingTicketId, setDraggingTicketId] = useState("");
  const [ticketDropTarget, setTicketDropTarget] = useState(null);
  const [draggingColumnId, setDraggingColumnId] = useState("");
  const [columnDropStageId, setColumnDropStageId] = useState("");
  const [columnDropPosition, setColumnDropPosition] = useState("");
  const [configStageId, setConfigStageId] = useState("");
  const [deletingFunnelId, setDeletingFunnelId] = useState("");
  const [editingFunnelId, setEditingFunnelId] = useState("");
  const [editingFunnelName, setEditingFunnelName] = useState("");
  const [editingFunnelAllowedUserIds, setEditingFunnelAllowedUserIds] = useState([]);
  const [deletingStageId, setDeletingStageId] = useState("");
  const [editingStageId, setEditingStageId] = useState("");
  const [editingStageName, setEditingStageName] = useState("");
  const [editingStageColor, setEditingStageColor] = useState(DEFAULT_STAGE_COLOR);
  const [stageColorPickerOpen, setStageColorPickerOpen] = useState(false);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceModalField, setSequenceModalField] = useState("");
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetFunnelId, setSheetFunnelId] = useState(NO_FUNNEL_SELECTION);
  const [sheetSpreadsheetId, setSheetSpreadsheetId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetSaving, setSheetSaving] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetStatus, setSheetStatus] = useState("");

  const selectedFunnel =
    funnels.find(funnel => String(funnel.id) === String(selectedFunnelId)) ||
    funnels[0] || {
      id: "",
      name: "",
      allowedUserIds: [],
      stages: [],
      ticketStages: {},
      ticketPositions: {},
    };

  const loadFunnels = async () => {
    const { data } = await api.get("/funnels");
    const loadedFunnels = normalizeFunnels(data.funnels || []);
    setFunnels(loadedFunnels);
    setSelectedFunnelId(prevState =>
      loadedFunnels.some(funnel => String(funnel.id) === String(prevState))
        ? prevState
        : loadedFunnels[0]?.id || ""
    );
  };

  const loadSequences = async () => {
    const loadedSequences = [];
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      const { data } = await api.get("/chatbot/sequences", {
        params: { searchParam: "", pageNumber },
      });
      loadedSequences.push(...(data.sequences || []));
      hasMore = Boolean(data.hasMore);
      pageNumber += 1;
    }

    setSequences(loadedSequences);
    return loadedSequences;
  };

  const loadUsers = async () => {
    const loadedUsers = [];
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      const { data } = await api.get("/users/", {
        params: { searchParam: "", pageNumber },
      });
      loadedUsers.push(...(data.users || []));
      hasMore = Boolean(data.hasMore);
      pageNumber += 1;
    }

    setUsers(loadedUsers);
    return loadedUsers;
  };

  const getUserName = userId =>
    users.find(item => Number(item.id) === Number(userId))?.name ||
    `Usuario ${userId}`;

  const loadSheetConfig = async funnelId => {
    if (!funnelId || funnelId === NO_FUNNEL_SELECTION) {
      setSheetSpreadsheetId("");
      setSheetName("");
      setSheetStatus("");
      return;
    }

    try {
      setSheetLoading(true);
      const { data } = await api.get("/funnels/sheets/config", {
        params: { funnelId },
      });
      setSheetSpreadsheetId(data.config?.spreadsheetId || "");
      setSheetName(data.config?.sheetName || "");
      setSheetStatus("");
    } catch (err) {
      toastError(err);
    } finally {
      setSheetLoading(false);
    }
  };

  const openSheetModal = () => {
    const defaultFunnelId = selectedFunnel.id
      ? String(selectedFunnel.id)
      : funnels[0]?.id
      ? String(funnels[0].id)
      : NO_FUNNEL_SELECTION;

    setSheetFunnelId(defaultFunnelId);
    setSheetOpen(true);
    loadSheetConfig(defaultFunnelId);
  };

  const saveSheetConfig = async (showToast = true) => {
    if (sheetFunnelId === NO_FUNNEL_SELECTION) {
      toast.error("Selecciona un embudo.");
      return null;
    }

    if (!sheetSpreadsheetId.trim()) {
      toast.error("Ingresa el ID del documento.");
      return null;
    }

    if (!sheetName.trim()) {
      toast.error("Ingresa el nombre de la hoja.");
      return null;
    }

    setSheetSaving(true);
    try {
      const { data } = await api.put("/funnels/sheets/config", {
        funnelId: Number(sheetFunnelId),
        spreadsheetId: sheetSpreadsheetId.trim(),
        sheetName: sheetName.trim(),
      });
      setSheetStatus("Configuracion guardada.");
      if (showToast) toast.success("Configuracion guardada.");
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setSheetSaving(false);
    }
  };

  const syncFunnelLeadsToSheet = async () => {
    const savedConfig = await saveSheetConfig(false);
    if (!savedConfig) return;

    try {
      setSheetSyncing(true);
      const { data } = await api.post("/funnels/sheets/sync", {
        funnelId: Number(sheetFunnelId),
      });
      toast.success("Leads enviados.");
      setSheetStatus(
        `${data.total || 0} lead(s) enviados en ${data.batches || 1} lote(s).`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSheetSyncing(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;

    const fetchFunnels = async () => {
      try {
        const { data } = await api.get("/funnels");
        if (cancelled) return;

        const loadedFunnels = normalizeFunnels(data.funnels || []);
        setFunnels(loadedFunnels);
        setSelectedFunnelId(loadedFunnels[0]?.id || "");
      } catch (err) {
        if (!cancelled) toastError(err);
      }
    };

    fetchFunnels();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!canManageFunnels) return undefined;

    let cancelled = false;

    loadUsers().catch(err => {
      if (!cancelled) toastError(err);
    });

    return () => {
      cancelled = true;
    };
  }, [canManageFunnels]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const socket = openSocket();

    socket.on("connect", () => {
      socket.emit("joinNotification");
    });

    socket.on("ticket", data => {
      if (data.action === "update" && data.ticket) {
        setTickets(prevState => {
          const ticketIndex = prevState.findIndex(
            ticket => Number(ticket.id) === Number(data.ticket.id)
          );

          if (ticketIndex === -1) return prevState;

          const nextTickets = [...prevState];
          nextTickets[ticketIndex] = data.ticket;
          return nextTickets;
        });
      }

      if (data.action === "delete") {
        setTickets(prevState =>
          prevState.filter(ticket => Number(ticket.id) !== Number(data.ticketId))
        );
      }
    });

    socket.on("funnel", data => {
      if (
        ["create", "update", "delete", "stage:create", "stage:update", "stage:delete"].includes(
          data.action
        )
      ) {
        loadFunnels().catch(() => undefined);
      }

      if (data.action === "ticket:move") {
        setFunnels(prevState =>
          prevState.map(funnel => {
            if (Number(funnel.id) !== Number(data.funnelId)) return funnel;

            const nextTicketStages = { ...funnel.ticketStages };
            const nextTicketPositions = { ...(funnel.ticketPositions || {}) };
            if (data.stageId) {
              nextTicketStages[String(data.ticketId)] = data.stageId;
              if (Array.isArray(data.orderedTicketIds)) {
                data.orderedTicketIds.forEach((ticketId, index) => {
                  nextTicketPositions[String(ticketId)] = index;
                });
              } else if (data.position !== undefined) {
                nextTicketPositions[String(data.ticketId)] = Number(data.position);
              }
            } else {
              delete nextTicketStages[String(data.ticketId)];
              delete nextTicketPositions[String(data.ticketId)];
            }

            return {
              ...funnel,
              ticketStages: nextTicketStages,
              ticketPositions: nextTicketPositions,
            };
          })
        );
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;

    const fetchQueueIds = async () => {
      const isAdmin = ["admin", "superadmin", "owner"].includes(
        String(user?.profile || "").toLowerCase()
      );

      if (!isAdmin) {
        return (user?.queues || []).map(queue => queue.id);
      }

      const { data } = await api.get("/queue");
      return (data || []).map(queue => queue.id);
    };

    const fetchTickets = async () => {
      setLoadingTickets(true);
      try {
        const queueIds = await fetchQueueIds();
        const { data } = await api.get("/tickets", {
          params: {
            pageNumber: 1,
            rowsPerPage: SOURCE_TICKETS_PAGE_SIZE,
            searchParam,
            showAll: true,
            queueIds: JSON.stringify(queueIds),
            labelIds: JSON.stringify([]),
            connectionIds: JSON.stringify([]),
            userIds: JSON.stringify([]),
          },
        });

        if (!cancelled) {
          setTickets(data.tickets || []);
          setTicketsCount(data.count || 0);
        }
      } catch (err) {
        if (!cancelled) toastError(err);
      } finally {
        if (!cancelled) setLoadingTickets(false);
      }
    };

    const timeout = setTimeout(fetchTickets, searchParam ? 350 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchParam, user]);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;

    loadSequences().catch(err => {
      if (!cancelled) toastError(err);
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const cards = useMemo(() => tickets.map(getTicketCard), [tickets]);

  const connectionOptions = useMemo(() => {
    const options = new Map();
    cards.forEach(card => {
      if (card.connectionId && card.connection) {
        options.set(String(card.connectionId), card.connection);
      }
    });
    return Array.from(options, ([id, name]) => ({ id, name }));
  }, [cards]);

  const queueOptions = useMemo(() => {
    const options = new Map();
    cards.forEach(card => {
      if (card.queueId && card.queue) {
        options.set(String(card.queueId), card.queue);
      }
    });
    return Array.from(options, ([id, name]) => ({ id, name }));
  }, [cards]);

  const agentOptions = useMemo(() => {
    const options = new Map();
    cards.forEach(card => {
      if (card.agentId && card.agent) {
        options.set(String(card.agentId), card.agent);
      }
    });
    return Array.from(options, ([id, name]) => ({ id, name }));
  }, [cards]);

  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      const matchesConnection =
        !selectedConnectionIds.length ||
        selectedConnectionIds.includes(String(card.connectionId));
      const matchesQueue =
        !selectedQueueIds.length ||
        selectedQueueIds.includes(String(card.queueId));
      const matchesAgent =
        !selectedAgentIds.length ||
        selectedAgentIds.includes(String(card.agentId));

      return matchesConnection && matchesQueue && matchesAgent;
    });
  }, [cards, selectedAgentIds, selectedConnectionIds, selectedQueueIds]);

  const cardsByStage = useMemo(() => {
    const result = {};

    selectedFunnel.stages.forEach(stage => {
      result[stage.id] = [];
    });

    filteredCards.forEach(card => {
      const stageId = selectedFunnel.ticketStages[card.id];
      if (stageId && result[stageId]) {
        result[stageId].push(card);
      }
    });

    Object.keys(result).forEach(stageId => {
      result[stageId].sort(
        (firstCard, secondCard) =>
          Number(selectedFunnel.ticketPositions?.[firstCard.id] || 0) -
          Number(selectedFunnel.ticketPositions?.[secondCard.id] || 0)
      );
    });

    return result;
  }, [filteredCards, selectedFunnel]);

  const sourceCards = useMemo(() => {
    const normalizedSearch = searchParam.trim().toLowerCase();

    return filteredCards.filter(card => {
      if (selectedFunnel.ticketStages[card.id]) return false;
      if (!normalizedSearch) return true;
      return card.contact.toLowerCase().includes(normalizedSearch);
    });
  }, [filteredCards, searchParam, selectedFunnel]);

  const handleAddFunnel = async event => {
    event.preventDefault();
    if (!canManageFunnels) return;
    const trimmedName = funnelName.trim();
    if (!trimmedName) return;

    try {
      const { data } = await api.post("/funnels", {
        name: trimmedName,
        allowedUserIds: [Number(user.id)],
      });
      await loadFunnels();
      setSelectedFunnelId(data.id);
      setFunnelName("");
      setSearchParam("");
    } catch (err) {
      toastError(err);
    }
  };

  const openEditFunnel = () => {
    if (!selectedFunnel.id || !canManageFunnels) return;
    setEditingFunnelId(selectedFunnel.id);
    setEditingFunnelName(selectedFunnel.name || "");
    setEditingFunnelAllowedUserIds(selectedFunnel.allowedUserIds || []);
  };

  const closeEditFunnel = () => {
    setEditingFunnelId("");
    setEditingFunnelName("");
    setEditingFunnelAllowedUserIds([]);
  };

  const handleEditFunnel = async event => {
    event.preventDefault();
    const trimmedName = editingFunnelName.trim();
    if (!editingFunnelId || !trimmedName) return;

    try {
      await api.put(`/funnels/${editingFunnelId}`, {
        name: trimmedName,
        allowedUserIds: editingFunnelAllowedUserIds,
      });
      await loadFunnels();
      closeEditFunnel();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteFunnel = async () => {
    if (!canManageFunnels) return;
    if (!deletingFunnelId) return;

    try {
      await api.delete(`/funnels/${deletingFunnelId}`);
      await loadFunnels();
      setDeletingFunnelId("");
      setSearchParam("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddStage = async event => {
    event.preventDefault();
    if (!canManageFunnels) return;
    const trimmedName = stageName.trim();
    if (!trimmedName || !selectedFunnel.id) return;

    try {
      await api.post(`/funnels/${selectedFunnel.id}/stages`, {
        name: trimmedName,
        color: getNextStageColor(selectedFunnel.stages),
      });
      await loadFunnels();
      setStageName("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteStage = async () => {
    if (!canManageFunnels) return;
    const stageId = deletingStageId;
    if (selectedFunnel.stages.length === 1) return;
    try {
      await api.delete(`/funnel-stages/${stageId}`);
      await loadFunnels();
      setDeletingStageId("");
    } catch (err) {
      toastError(err);
    }
  };

  const openEditStage = stage => {
    if (!canManageFunnels) return;
    setEditingStageId(stage.id);
    setEditingStageName(stage.name || "");
    setEditingStageColor(stage.color || DEFAULT_STAGE_COLOR);
  };

  const closeEditStage = () => {
    setEditingStageId("");
    setEditingStageName("");
    setEditingStageColor(DEFAULT_STAGE_COLOR);
  };

  const handleEditStage = async event => {
    event.preventDefault();
    if (!canManageFunnels) return;
    const stage = selectedFunnel.stages.find(
      item => String(item.id) === String(editingStageId)
    );
    const trimmedName = editingStageName.trim();

    if (!stage || !trimmedName) return;

    try {
      await api.put(`/funnel-stages/${stage.id}`, {
        name: trimmedName,
        color: editingStageColor,
        position: stage.position,
        onEnterSequenceId: stage.onEnterSequenceId || null,
        onLeaveSequenceId: stage.onLeaveSequenceId || null,
      });
      await loadFunnels();
      closeEditStage();
    } catch (err) {
      toastError(err);
    }
  };

  const moveTicketToStage = async ({
    ticketId,
    stageId,
    targetTicketId = null,
    placement = "after",
  }) => {
    if (!ticketId || !selectedFunnel.id) return;
    const nextStageId = stageId === SOURCE_STAGE_ID ? null : stageId;
    const targetStageCards = nextStageId ? [...(cardsByStage[nextStageId] || [])] : [];
    let orderedTicketIds = targetStageCards
      .map(card => String(card.id))
      .filter(id => id !== String(ticketId));

    if (nextStageId) {
      if (targetTicketId) {
        const targetIndex = orderedTicketIds.findIndex(
          id => id === String(targetTicketId)
        );
        const insertIndex =
          targetIndex < 0
            ? orderedTicketIds.length
            : targetIndex + (placement === "after" ? 1 : 0);
        orderedTicketIds.splice(insertIndex, 0, String(ticketId));
      } else {
        orderedTicketIds.push(String(ticketId));
      }
    }
    const nextPosition = nextStageId
      ? Math.max(orderedTicketIds.indexOf(String(ticketId)), 0)
      : undefined;

    setFunnels(prevState =>
      prevState.map(funnel => {
        if (Number(funnel.id) !== Number(selectedFunnel.id)) return funnel;

        const nextTicketStages = { ...funnel.ticketStages };
        const nextTicketPositions = { ...(funnel.ticketPositions || {}) };
        if (nextStageId) {
          nextTicketStages[String(ticketId)] = nextStageId;
          orderedTicketIds.forEach((orderedTicketId, index) => {
            nextTicketPositions[String(orderedTicketId)] = index;
          });
        } else {
          delete nextTicketStages[String(ticketId)];
          delete nextTicketPositions[String(ticketId)];
        }

        return {
          ...funnel,
          ticketStages: nextTicketStages,
          ticketPositions: nextTicketPositions,
        };
      })
    );
    setDraggingStageId("");
    setDraggingTicketId("");
    setTicketDropTarget(null);

    try {
      await api.put(`/funnels/${selectedFunnel.id}/tickets/${ticketId}/move`, {
        stageId: nextStageId,
        position: nextPosition,
        orderedTicketIds,
      });
    } catch (err) {
      toastError(err);
      await loadFunnels();
    }
  };

  const handleDrop = stageId => async event => {
    event.preventDefault();
    const ticketId =
      event.dataTransfer.getData(TICKET_DRAG_TYPE) ||
      event.dataTransfer.getData("text/plain");

    await moveTicketToStage({ ticketId, stageId });
  };

  const handleStageDragStart = stageId => event => {
    if (!canManageFunnels) return;
    event.dataTransfer.setData(STAGE_DRAG_TYPE, String(stageId));
    event.dataTransfer.effectAllowed = "move";
    setDraggingColumnId(String(stageId));
  };

  const handleStageDragEnd = () => {
    setDraggingColumnId("");
    setColumnDropStageId("");
    setColumnDropPosition("");
  };

  const handleStageColumnDragOver = stageId => event => {
    if (!draggingColumnId) {
      event.preventDefault();
      return;
    }

    if (String(stageId) !== String(draggingColumnId)) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const nextDropStageId = String(stageId);
      const nextDropPosition =
        event.clientX > bounds.left + bounds.width / 2 ? "after" : "before";

      setColumnDropStageId(prevState =>
        prevState === nextDropStageId ? prevState : nextDropStageId
      );
      setColumnDropPosition(prevState =>
        prevState === nextDropPosition ? prevState : nextDropPosition
      );
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleStageColumnDrop = targetStage => async event => {
    const draggedStageId = event.dataTransfer.getData(STAGE_DRAG_TYPE);
    if (draggedStageId) {
      event.preventDefault();
      event.stopPropagation();

      if (String(draggedStageId) === String(targetStage.id) || !selectedFunnel.id) {
        handleStageDragEnd();
        return;
      }

      const currentStages = selectedFunnel.stages;
      const sourceIndex = currentStages.findIndex(
        stage => String(stage.id) === String(draggedStageId)
      );
      const targetIndex = currentStages.findIndex(
        stage => String(stage.id) === String(targetStage.id)
      );

      if (sourceIndex < 0 || targetIndex < 0) {
        handleStageDragEnd();
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const shouldInsertAfter = event.clientX > bounds.left + bounds.width / 2;
      let insertIndex = targetIndex + (shouldInsertAfter ? 1 : 0);
      const nextStages = [...currentStages];
      const [movedStage] = nextStages.splice(sourceIndex, 1);

      if (sourceIndex < insertIndex) insertIndex -= 1;
      nextStages.splice(insertIndex, 0, movedStage);

      const positionedStages = nextStages.map((stage, index) => ({
        ...stage,
        position: index,
      }));

      setFunnels(prevState =>
        prevState.map(funnel =>
          Number(funnel.id) === Number(selectedFunnel.id)
            ? { ...funnel, stages: positionedStages }
            : funnel
        )
      );
      handleStageDragEnd();

      try {
        await Promise.all(
          positionedStages.map(stage =>
            api.put(`/funnel-stages/${stage.id}`, {
              name: stage.name,
              color: stage.color || DEFAULT_STAGE_COLOR,
              position: stage.position,
              onEnterSequenceId: stage.onEnterSequenceId || null,
              onLeaveSequenceId: stage.onLeaveSequenceId || null,
            })
          )
        );
        await loadFunnels();
      } catch (err) {
        toastError(err);
        await loadFunnels();
      }
      return;
    }

    handleDrop(targetStage.id)(event);
  };

  const handleUpdateStageConfig = async (field, value, stage = configStage) => {
    if (!canManageFunnels) return;
    if (!stage) return;

    try {
      const nextStage = { ...stage, [field]: value };
      await api.put(`/funnel-stages/${stage.id}`, {
        name: nextStage.name,
        color: nextStage.color || DEFAULT_STAGE_COLOR,
        position: nextStage.position,
        onEnterSequenceId: nextStage.onEnterSequenceId || null,
        onLeaveSequenceId: nextStage.onLeaveSequenceId || null,
      });
      await loadFunnels();
    } catch (err) {
      toastError(err);
    }
  };

  const configStage = selectedFunnel.stages.find(stage => stage.id === configStageId);
  const deletingFunnel = funnels.find(
    funnel => String(funnel.id) === String(deletingFunnelId)
  );
  const editingFunnel = funnels.find(
    funnel => String(funnel.id) === String(editingFunnelId)
  );
  const deletingStage = selectedFunnel.stages.find(
    stage => String(stage.id) === String(deletingStageId)
  );
  const editingStage = selectedFunnel.stages.find(
    stage => String(stage.id) === String(editingStageId)
  );

  const openCreateSequence = field => {
    setSequenceModalField(field);
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (field, sequenceId) => {
    if (!sequenceId) return;
    setSequenceModalField(field);
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = async savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    const targetField = sequenceModalField;
    const targetStage = configStage;

    try {
      const nextSequences = await loadSequences();

      if (
        savedSequenceId &&
        savedSequence &&
        !nextSequences.some(sequence => String(sequence.id) === String(savedSequenceId))
      ) {
        setSequences(prevSequences => [...prevSequences, savedSequence]);
      }

      if (targetField && savedSequenceId && targetStage) {
        await handleUpdateStageConfig(targetField, String(savedSequenceId), targetStage);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSequenceModalOpen(false);
      setSequenceModalId(null);
      setSequenceModalField("");
    }
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceModalField("");
  };

  const formatSequenceOption = sequence =>
    `ID: ${sequence.id} - ${sequence.name}`;

  const getSequenceModalInitialName = () => {
    if (!configStage) return "";
    return sequenceModalField === "onLeaveSequenceId"
      ? `Al quitar de ${configStage.name}`
      : `Al poner en ${configStage.name}`;
  };

  const renderFilterValue = (selected, options, fallback) => {
    if (!selected.length) return fallback;
    return options
      .filter(option => selected.includes(String(option.id)))
      .map(option => option.name)
      .join(", ");
  };

  const renderSequenceSelector = ({ field, label }) => {
    const selectedSequenceId = configStage?.[field] || "";

    return (
      <>
        <FormControl
          className={classes.modalControl}
          fullWidth
          margin="dense"
          variant="outlined"
        >
          <InputLabel>{label}</InputLabel>
          <Select
            value={selectedSequenceId || NO_STAGE_SEQUENCE}
            label={label}
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(field);
                return;
              }

              handleUpdateStageConfig(
                field,
                event.target.value === NO_STAGE_SEQUENCE ? "" : event.target.value
              );
            }}
          >
            <MenuItem value={NO_STAGE_SEQUENCE}>No ejecutar secuencia</MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences.map(sequence => (
              <MenuItem key={sequence.id} value={String(sequence.id)}>
                {formatSequenceOption(sequence)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={classes.sequenceActions}>
          <Button
            color="primary"
            disabled={!selectedSequenceId}
            onClick={() => openEditSequence(field, selectedSequenceId)}
          >
            Editar secuencia
          </Button>
        </div>
      </>
    );
  };

  const renderCard = (card, stageId = SOURCE_STAGE_ID) => (
    <Paper
      key={card.id}
      className={`${classes.ticketCard} ${
        ticketDropTarget?.ticketId === String(card.id) &&
        ticketDropTarget?.placement === "before"
          ? classes.ticketDropBefore
          : ""
      } ${
        ticketDropTarget?.ticketId === String(card.id) &&
        ticketDropTarget?.placement === "after"
          ? classes.ticketDropAfter
          : ""
      }`}
      draggable
      elevation={0}
      onDragStart={event => {
        event.dataTransfer.setData(TICKET_DRAG_TYPE, card.id);
        event.dataTransfer.setData("text/plain", card.id);
        event.dataTransfer.effectAllowed = "move";
        setDraggingTicketId(String(card.id));
      }}
      onDragEnd={() => {
        setDraggingStageId("");
        setDraggingTicketId("");
        setTicketDropTarget(null);
      }}
      onDragOver={event => {
        if (!draggingTicketId || stageId === SOURCE_STAGE_ID) return;
        event.preventDefault();
        event.stopPropagation();

        const bounds = event.currentTarget.getBoundingClientRect();
        const placement =
          event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
        const nextTarget = {
          stageId: String(stageId),
          ticketId: String(card.id),
          placement,
        };

        setTicketDropTarget(prevState =>
          prevState?.stageId === nextTarget.stageId &&
          prevState?.ticketId === nextTarget.ticketId &&
          prevState?.placement === nextTarget.placement
            ? prevState
            : nextTarget
        );
      }}
      onDrop={event => {
        const draggedTicketId = event.dataTransfer.getData(TICKET_DRAG_TYPE);
        if (!draggedTicketId || stageId === SOURCE_STAGE_ID) return;

        event.preventDefault();
        event.stopPropagation();

        moveTicketToStage({
          ticketId: draggedTicketId,
          stageId,
          targetTicketId: card.id,
          placement: ticketDropTarget?.placement || "after",
        });
      }}
    >
      <span
        className={classes.ticketQueueColor}
        style={{ backgroundColor: card.queueColor }}
      />
      <ListItem dense className={classes.ticketListItem}>
        <DragIndicatorIcon className={classes.dragIcon} fontSize="small" />
        <ListItemAvatar>
          <Avatar
            src={privacyCensorSettings.contactPhotos ? undefined : card.profilePicUrl}
            className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
          >
            {privacyCensorSettings.contactNames ? undefined : card.contact[0]}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <>
              <span className={classes.ticketTopLine}>
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="textPrimary"
                  className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
                >
                  {privacyCensorSettings.contactNames
                    ? "Contacto privado"
                    : card.contact}
                </Typography>
                <span className={classes.labelDots}>
                  {card.labels.slice(0, 5).map(label => (
                    <Tooltip key={label.id} title={label.name || "Etiqueta"}>
                      <span
                        className={classes.labelDot}
                        style={{ backgroundColor: label.color || "#7C7C7C" }}
                      />
                    </Tooltip>
                  ))}
                </span>
              </span>
              <Typography
                className={
                  privacyCensorSettings.contactNames
                    ? classes.censoredText
                    : classes.ticketNumber
                }
                noWrap
                variant="body2"
              >
                {privacyCensorSettings.contactNames
                  ? "Telefono privado"
                  : card.number || "Sin numero"}
              </Typography>
            </>
          }
        />
      </ListItem>
      <Tooltip title="Ir al chat">
        <IconButton
          className={classes.openChatButton}
          size="small"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            history.push(`/tickets/${card.id}`);
          }}
          onMouseDown={event => event.stopPropagation()}
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <div className={classes.ticketMetaTags}>
        {card.queue && (
          <span
            className={classes.tag}
            title="Área"
            style={{
              backgroundColor: card.queueColor,
              color: getReadableTextColor(card.queueColor),
            }}
          >
            <span className={classes.tagText}>{card.queue}</span>
          </span>
        )}
        {card.agent && (
          <span className={`${classes.tag} ${classes.agentTag}`} title="Agente">
            <Avatar
              src={card.agentProfilePicUrl}
              className={classes.tagAvatar}
            >
              {card.agent[0]}
            </Avatar>
            <span className={classes.tagText}>{card.agent}</span>
          </span>
        )}
        {card.connection && (
          <span
            className={`${classes.tag} ${classes.connectionTag}`}
            title="Conexion"
          >
            <WhatsAppIcon className={classes.tagAvatar} />
            <span className={classes.tagText}>{card.connection}</span>
          </span>
        )}
      </div>
    </Paper>
  );

  return (
    <MainContainer>
      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={
          selectedConnectionIds.length === 1 ? selectedConnectionIds[0] : ""
        }
        initialSequenceName={getSequenceModalInitialName()}
      />
      <MainHeader>
        <div className={classes.headerContent}>
        <Title>Embudos</Title>
        <MainHeaderButtonsWrapper className={classes.headerActions}>
          <div className={classes.headerControls}>
            <div className={classes.funnelSelectorGroup}>
              <FormControl
                className={classes.selector}
                margin="dense"
                size="small"
                variant="outlined"
              >
                <InputLabel>Embudo</InputLabel>
                <Select
                  value={selectedFunnel.id || ""}
                  label="Embudo"
                  onChange={event => {
                    setSelectedFunnelId(event.target.value);
                    setSearchParam("");
                  }}
                >
                  {!funnels.length && (
                    <MenuItem value="" disabled>
                      Sin embudos
                    </MenuItem>
                  )}
                  {funnels.map(funnel => (
                    <MenuItem key={funnel.id} value={funnel.id}>
                      {funnel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {canManageFunnels && (
                <>
                  <Tooltip title="Editar embudo">
                    <span>
                      <IconButton
                        className={classes.funnelActionButton}
                        size="small"
                        onClick={openEditFunnel}
                        disabled={!selectedFunnel.id}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Eliminar embudo">
                    <span>
                      <IconButton
                        className={classes.funnelActionButton}
                        size="small"
                        onClick={() => setDeletingFunnelId(selectedFunnel.id)}
                        disabled={!selectedFunnel.id}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </div>

            {canManageFunnels && (
              <>
                <form className={classes.inlineForm} onSubmit={handleAddFunnel}>
                  <TextField
                    className={classes.addInput}
                    size="small"
                    variant="outlined"
                    value={funnelName}
                    placeholder="Nuevo embudo"
                    onChange={event => setFunnelName(event.target.value)}
                  />
                  <Button
                    color="primary"
                    type="submit"
                    variant="outlined"
                    startIcon={<AddIcon />}
                  >
                    Embudo
                  </Button>
                </form>

                <form className={classes.inlineForm} onSubmit={handleAddStage}>
                  <TextField
                    className={classes.addInput}
                    size="small"
                    variant="outlined"
                    value={stageName}
                    placeholder="Nueva etapa"
                    onChange={event => setStageName(event.target.value)}
                  />
                  <Button
                    color="primary"
                    type="submit"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Etapa
                  </Button>
                </form>

                <Tooltip title="Configurar Google Sheets">
                  <span>
                    <IconButton
                      className={classes.funnelActionButton}
                      color="primary"
                      size="small"
                      onClick={openSheetModal}
                      disabled={!funnels.length}
                      aria-label="Configurar Google Sheets"
                    >
                      <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            <FormControl
              className={classes.filterControl}
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel>Conexión</InputLabel>
              <Select
                multiple
                value={selectedConnectionIds}
                label="Conexión"
                onChange={event => setSelectedConnectionIds(event.target.value)}
                renderValue={selected =>
                  renderFilterValue(selected, connectionOptions, "Conexión")
                }
              >
                {connectionOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox checked={selectedConnectionIds.includes(option.id)} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              className={classes.filterControl}
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel>Área</InputLabel>
              <Select
                multiple
                value={selectedQueueIds}
                label="Área"
                onChange={event => setSelectedQueueIds(event.target.value)}
                renderValue={selected =>
                  renderFilterValue(selected, queueOptions, "Área")
                }
              >
                {queueOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox checked={selectedQueueIds.includes(option.id)} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              className={classes.filterControl}
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel>Asignado</InputLabel>
              <Select
                multiple
                value={selectedAgentIds}
                label="Asignado"
                onChange={event => setSelectedAgentIds(event.target.value)}
                renderValue={selected =>
                  renderFilterValue(selected, agentOptions, "Asignado")
                }
              >
                {agentOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox checked={selectedAgentIds.includes(option.id)} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      <Paper className={classes.boardPaper} variant="outlined">
        <div className={classes.board}>
          <Paper
            className={`${classes.stage} ${classes.sourceStage} ${
              draggingStageId === SOURCE_STAGE_ID ? classes.stageDraggingOver : ""
            }`}
            elevation={0}
            onDragOver={event => event.preventDefault()}
            onDragEnter={() => setDraggingStageId(SOURCE_STAGE_ID)}
            onDragLeave={() => setDraggingStageId("")}
            onDrop={handleDrop(SOURCE_STAGE_ID)}
          >
            <div className={`${classes.stageHeader} ${classes.sourceHeader}`}>
              <div className={classes.stageTitleRow}>
                <Typography className={classes.stageTitle} variant="subtitle1">
                  Chats
                </Typography>
                <Chip size="small" label={ticketsCount} />
                {loadingTickets && <CircularProgress size={18} />}
              </div>
              <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                value={searchParam}
                placeholder="Buscar por contacto"
                onChange={event => setSearchParam(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className={classes.stageBody}>
              {sourceCards.length ? (
                sourceCards.map(card => renderCard(card, SOURCE_STAGE_ID))
              ) : (
                <div className={classes.emptyStage}>
                  {loadingTickets ? "Cargando chats..." : "No hay chats para mostrar"}
                </div>
              )}
            </div>
          </Paper>

          {selectedFunnel.stages.map(stage => {
            const stageCards = cardsByStage[stage.id] || [];
            const stageColors = getStageColors(stage.color);

            return (
              <Paper
                key={stage.id}
                className={`${classes.stage} ${
                  draggingStageId === stage.id ? classes.stageDraggingOver : ""
                } ${
                  columnDropStageId === String(stage.id)
                    ? classes.stageColumnDraggingOver
                    : ""
                } ${
                  columnDropStageId === String(stage.id) &&
                  columnDropPosition === "before"
                    ? classes.stageColumnDropBefore
                    : ""
                } ${
                  columnDropStageId === String(stage.id) &&
                  columnDropPosition === "after"
                    ? classes.stageColumnDropAfter
                    : ""
                }`}
                style={{
                  backgroundColor: stageColors.base,
                  borderColor: stageColors.border,
                }}
                elevation={0}
                onDragOver={handleStageColumnDragOver(stage.id)}
                onDragEnter={() => {
                  if (!draggingColumnId) setDraggingStageId(stage.id);
                }}
                onDragLeave={() => {
                  if (!draggingColumnId) setDraggingStageId("");
                }}
                onDrop={handleStageColumnDrop(stage)}
              >
                <span
                  className={classes.stageColorBar}
                  style={{ backgroundColor: stageColors.accent }}
                />
                <div
                  className={classes.stageHeader}
                  style={{
                    backgroundColor: stageColors.header,
                    borderBottomColor: stageColors.border,
                  }}
                >
                  {canManageFunnels && (
                    <Tooltip title="Mover etapa">
                      <IconButton
                        className={classes.stageDragButton}
                        size="small"
                        style={{ color: stageColors.mutedIcon }}
                        draggable
                        onDragStart={handleStageDragStart(stage.id)}
                        onDragEnd={handleStageDragEnd}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Typography
                    className={classes.stageTitle}
                    variant="subtitle1"
                    style={{ color: stageColors.accent }}
                  >
                    {stage.name}
                  </Typography>
                  <Chip
                    className={classes.stageCount}
                    size="small"
                    label={stageCards.length}
                    style={{
                      backgroundColor: stageColors.chipBackground,
                      color: stageColors.chipText,
                      borderColor: stageColors.border,
                    }}
                  />
                  {canManageFunnels && (
                    <>
                      <Tooltip title="Editar etapa">
                        <IconButton size="small" style={{ color: stageColors.mutedIcon }} onClick={() => openEditStage(stage)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar etapa">
                        <span>
                          <IconButton
                            size="small"
                            style={{ color: stageColors.mutedIcon }}
                            disabled={selectedFunnel.stages.length === 1}
                            onClick={() => setDeletingStageId(stage.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Configurar secuencias">
                        <IconButton size="small" style={{ color: stageColors.mutedIcon }} onClick={() => setConfigStageId(stage.id)}>
                          <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
                <div className={classes.stageBody}>
                  {stageCards.length ? (
                    stageCards.map(card => renderCard(card, stage.id))
                  ) : (
                    <div className={classes.emptyStage}>Sin chats en esta etapa</div>
                  )}
                </div>
              </Paper>
            );
          })}
        </div>
      </Paper>

      <Dialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Google Sheets</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth margin="dense" variant="outlined" disabled={sheetLoading}>
            <InputLabel>Embudo</InputLabel>
            <Select
              label="Embudo"
              value={sheetFunnelId || NO_FUNNEL_SELECTION}
              onChange={event => {
                const nextFunnelId = event.target.value;
                setSheetFunnelId(nextFunnelId);
                loadSheetConfig(nextFunnelId);
              }}
            >
              <MenuItem value={NO_FUNNEL_SELECTION}>Seleccionar embudo</MenuItem>
              {funnels.map(funnel => (
                <MenuItem key={funnel.id} value={String(funnel.id)}>
                  {funnel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className={classes.sheetConfigFields}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="ID del documento"
              value={sheetSpreadsheetId}
              disabled={sheetLoading}
              onChange={event => setSheetSpreadsheetId(event.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="Hoja"
              value={sheetName}
              disabled={sheetLoading}
              onChange={event => setSheetName(event.target.value)}
            />
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.sheetConfigStatus}
          >
            {sheetStatus}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSheetOpen(false)}>Cerrar</Button>
          <Button
            color="primary"
            variant="outlined"
            disabled={sheetLoading || sheetSaving || sheetSyncing}
            onClick={() => saveSheetConfig(true)}
          >
            {sheetSaving ? "Guardando..." : "Guardar"}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={sheetLoading || sheetSaving || sheetSyncing}
            onClick={syncFunnelLeadsToSheet}
          >
            {sheetSyncing ? "Enviando..." : "Guardar y enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingFunnel)}
        onClose={closeEditFunnel}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleEditFunnel}>
          <DialogTitle>Editar embudo</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              variant="outlined"
              label="Nombre"
              value={editingFunnelName}
              onChange={event => setEditingFunnelName(event.target.value)}
            />
            <FormControl fullWidth margin="dense" variant="outlined">
              <InputLabel>Usuarios permitidos</InputLabel>
              <Select
                multiple
                label="Usuarios permitidos"
                value={
                  editingFunnelAllowedUserIds.length
                    ? editingFunnelAllowedUserIds
                    : [ALL_FUNNEL_USERS]
                }
                renderValue={selected =>
                  selected.includes(ALL_FUNNEL_USERS)
                    ? "Todos los usuarios"
                    : selected.map(getUserName).join(", ")
                }
                onChange={event => {
                  const rawValue = event.target.value || [];
                  const selectedIds =
                    rawValue[rawValue.length - 1] === ALL_FUNNEL_USERS
                      ? []
                      : rawValue
                          .filter(value => value !== ALL_FUNNEL_USERS)
                          .map(Number)
                          .filter(Boolean);
                  setEditingFunnelAllowedUserIds(selectedIds);
                }}
              >
                <MenuItem value={ALL_FUNNEL_USERS}>
                  <Checkbox checked={!editingFunnelAllowedUserIds.length} />
                  <ListItemText primary="Todos los usuarios" />
                </MenuItem>
                {users.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox
                      checked={editingFunnelAllowedUserIds.includes(Number(item.id))}
                    />
                    <ListItemText primary={item.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditFunnel}>Cancelar</Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={!editingFunnelName.trim()}
            >
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(deletingFunnel)}
        onClose={() => setDeletingFunnelId("")}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar embudo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Deseas eliminar el embudo {deletingFunnel?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFunnelId("")}>Cancelar</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleDeleteFunnel}
            disabled={!deletingFunnel}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingStage)}
        onClose={closeEditStage}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleEditStage}>
          <DialogTitle>Editar etapa</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              variant="outlined"
              label="Nombre"
              value={editingStageName}
              onChange={event => setEditingStageName(event.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="Color"
              value={editingStageColor}
              onChange={event => setEditingStageColor(event.target.value)}
              onBlur={event => {
                if (!isValidHexColor(event.target.value)) return;
                const color = normalizeHexColor(event.target.value);
                setEditingStageColor(color);
                saveCustomColor(color);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <div
                      className={classes.colorAdornment}
                      style={{ backgroundColor: editingStageColor }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => setStageColorPickerOpen(true)}
                  >
                    <ColorizeIcon />
                  </IconButton>
                ),
              }}
            />
            <ColorPicker
              open={stageColorPickerOpen}
              currentColor={editingStageColor}
              handleClose={() => setStageColorPickerOpen(false)}
              onChange={setEditingStageColor}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditStage}>Cancelar</Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={!editingStageName.trim()}
            >
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(deletingStage)}
        onClose={() => setDeletingStageId("")}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar etapa</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Deseas eliminar la etapa {deletingStage?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingStageId("")}>Cancelar</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleDeleteStage}
            disabled={!deletingStage || selectedFunnel.stages.length === 1}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(configStage)}
        onClose={() => setConfigStageId("")}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Configurar etapa</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            {configStage?.name}
          </Typography>
          {renderSequenceSelector({
            field: "onEnterSequenceId",
            label: "Al poner en la etapa",
          })}
          {renderSequenceSelector({
            field: "onLeaveSequenceId",
            label: "Al quitar de la etapa",
          })}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setConfigStageId("")}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Funnels;

